(() => {
  "use strict";

  const ROOT_SELECTOR = ".msi-ranking-root";
  const CACHE_TTL_MS = 30 * 60 * 1000;
  const CACHE_STALE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
  const REQUEST_TIMEOUT_MS = 15000;

  // Profile prefetch: compatible con mf-player-profile-cache-v1.js.
  const PROFILE_CACHE_VERSION = "1";
  const PROFILE_CACHE_PREFIX = "mf_profile_cache_v1:";
  const PROFILE_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const ACTIVE_SEASON_CACHE_KEY = "mf_active_season_cache_v1";

  // Estado competitivo compartido entre Ranking + Hero.
  const COMPETITIVE_STATE_KEY = "mf_competitive:current";
  const COMPETITIVE_EVENT = "mf:competitive-data-ready";

  const PROFILE_PREFETCH_INITIAL = 1;
  const PROFILE_PREFETCH_CONCURRENCY = 1;

  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.msiRankingInitialized === "true") return;

      root.dataset.msiRankingInitialized = "true";
      initRankingModule(root);
    });
  }

  function initRankingModule(root) {
    const config = {
      sectionId: root.dataset.sectionId || "",

      apiUrl: String(root.dataset.apiUrl || "").trim(),
      defaultSeason: String(root.dataset.defaultSeason || "").trim(),
      seasonQueryParam:
        String(root.dataset.seasonQueryParam || "season").trim() || "season",

      visibleRowsInitial: Math.max(
        1,
        parseInt(root.dataset.visibleRowsInitial || "24", 10) || 24
      ),

      profileBaseUrl:
        String(root.dataset.profileBaseUrl || "").trim() ||
        "/pages/perfil-de-competidor",

      playerIdQueryParam:
        String(root.dataset.playerIdQueryParam || "id").trim() || "id",

      loadingText:
        root.dataset.loadingText || "Cargando ranking…",

      emptyText:
        root.dataset.emptyText || "No hay jugadores para mostrar.",

      apiErrorText:
        root.dataset.apiErrorText || "No se pudo cargar el ranking.",

      seasonsErrorText:
        root.dataset.seasonsErrorText ||
        "No se pudieron cargar las temporadas.",

      cacheStatusText:
        root.dataset.cacheStatusText ||
        "Mostrando datos guardados temporalmente.",

      updatedStatusText:
        root.dataset.updatedStatusText || "Ranking actualizado.",

      loadMoreLabel:
        root.dataset.loadMoreLabel || "Ver más jugadores",

      loadLessLabel:
        root.dataset.loadLessLabel || "Ver menos jugadores",
    };

    const elements = {
      tableBody: root.querySelector("[data-ranking-table-body]"),
      mobileList: root.querySelector("[data-ranking-mobile]"),
      loadMoreWrap: root.querySelector("[data-ranking-loadmore-wrap]"),
      loadMoreBtn: root.querySelector("[data-ranking-loadmore]"),
      searchInput: root.querySelector("[data-ranking-search]"),
      statusNode: root.querySelector("[data-ranking-status]"),
      seasonButtonsWrap: root.querySelector(
        "[data-ranking-season-buttons]"
      ),
    };

    if (
      !elements.tableBody ||
      !elements.mobileList ||
      !elements.loadMoreWrap ||
      !elements.loadMoreBtn ||
      !elements.searchInput ||
      !elements.statusNode ||
      !elements.seasonButtonsWrap
    ) {
      return;
    }

    const state = {
      players: [],
      seasons: [],
      activeSeason: "",
      expanded: false,
      filterText: "",
      rankingRequestId: 0,
      prefetchQueue: [],
      prefetchQueued: new Set(),
      prefetchActive: 0,
    };

    init();

    async function init() {
      if (!isValidApiUrl(config.apiUrl)) {
        showError(config.apiErrorText);
        return;
      }

      readInitialUrlState();
      attachEvents();
      setLoadingState();

      await loadSeasons();

      if (!state.activeSeason) {
        state.activeSeason =
          chooseDefaultSeason(state.seasons) ||
          config.defaultSeason ||
          "";
      }

      if (!state.activeSeason) {
        showError(config.seasonsErrorText);
        return;
      }

      renderSeasonButtons();
      await loadRanking(state.activeSeason);
    }

    function readInitialUrlState() {
      const url = new URL(window.location.href);

      const seasonFromUrl = String(
        url.searchParams.get(config.seasonQueryParam) || ""
      ).trim();

      const qFromUrl = String(url.searchParams.get("q") || "").trim();

      state.activeSeason =
        seasonFromUrl || config.defaultSeason || "";

      if (qFromUrl) {
        state.filterText = normalizeSearchText(qFromUrl);
        elements.searchInput.value = qFromUrl;
        state.expanded = true;
      }
    }

    function attachEvents() {
      elements.searchInput.addEventListener("input", (event) => {
        const rawValue = String(event.target.value || "").trim();

        state.filterText = normalizeSearchText(rawValue);
        state.expanded = Boolean(state.filterText);

        setQueryParam("q", rawValue);
        renderRanking(getFilteredPlayers());
      });

      elements.loadMoreBtn.addEventListener("click", () => {
        state.expanded = !state.expanded;

        renderRanking(getFilteredPlayers());

        if (!state.expanded) {
          scrollToRankingTop();
        }
      });
    }

    async function loadSeasons() {
      const cacheKey = getCacheKey("seasons");
      const cached = getCache(cacheKey);

      if (cached && isCacheFresh(cached)) {
        const cachedSeasons = normalizeSeasonsPayload(cached.data);

        if (cachedSeasons.length) {
          state.seasons = cachedSeasons;
        }
      }

      try {
        const payload = await fetchJson(
          buildApiUrl("seasons")
        );

        const seasons = normalizeSeasonsPayload(payload);

        if (!seasons.length) {
          throw new Error("EMPTY_SEASONS");
        }

        state.seasons = seasons;
        setCache(cacheKey, payload);
      } catch (error) {
        if (!state.seasons.length && config.defaultSeason) {
          state.seasons = [
            {
              id: config.defaultSeason,
              name: "Temporada actual",
              status: "ACTIVA",
              order: 1,
              tournamentsRegistered: 0,
              tournamentsExpected: 18,
              active: true,
            },
          ];

          setStatus(config.seasonsErrorText);
        }
      }

      if (
        state.activeSeason &&
        !state.seasons.some(
          (season) => season.id === state.activeSeason
        )
      ) {
        state.activeSeason = "";
      }

      if (!state.activeSeason) {
        state.activeSeason =
          chooseDefaultSeason(state.seasons) ||
          config.defaultSeason ||
          "";
      }

      renderSeasonButtons();
    }

    async function loadRanking(seasonId) {
      const cleanSeasonId = String(seasonId || "").trim();

      if (!cleanSeasonId) {
        showError(config.apiErrorText);
        return;
      }

      const requestId = ++state.rankingRequestId;

      state.activeSeason = cleanSeasonId;
      state.expanded = Boolean(state.filterText);
      writeProfileCompatibleSeasonCache(cleanSeasonId);

      highlightActiveSeasonButton();
      setQueryParam(config.seasonQueryParam, cleanSeasonId);
      setLoadingState();

      const cacheKey = getCacheKey(
        `ranking:${cleanSeasonId}`
      );

      const cached = getCache(cacheKey);
      let renderedFromCache = false;

      if (cached && isCacheUsable(cached)) {
        try {
          const cachedPlayers = normalizeRankingPayload(
            cached.data,
            cleanSeasonId
          );

          state.players = cachedPlayers;
          renderRanking(getFilteredPlayers());

          setStatus(
            isCacheFresh(cached)
              ? config.cacheStatusText
              : "Mostrando la última clasificación guardada mientras se actualiza."
          );

          renderedFromCache = true;
          publishCompetitiveState(
            cleanSeasonId,
            cachedPlayers,
            "cache"
          );
          scheduleInitialProfilePrefetch(cachedPlayers);
        } catch (error) {
          renderedFromCache = false;
        }
      }

      try {
        const payload = await fetchJson(
          buildApiUrl("ranking", {
            season: cleanSeasonId,
          })
        );

        if (requestId !== state.rankingRequestId) {
          return;
        }

        const players = normalizeRankingPayload(
          payload,
          cleanSeasonId
        );

        state.players = players;

        setCache(cacheKey, payload);

        renderRanking(getFilteredPlayers());
        setStatus(config.updatedStatusText);
        publishCompetitiveState(
          cleanSeasonId,
          players,
          "network"
        );
        scheduleInitialProfilePrefetch(players);
      } catch (error) {
        if (requestId !== state.rankingRequestId) {
          return;
        }

        if (renderedFromCache) {
          setStatus(config.cacheStatusText);
          return;
        }

        const staleCache = getCache(cacheKey);

        if (staleCache && staleCache.data) {
          try {
            state.players = normalizeRankingPayload(
              staleCache.data,
              cleanSeasonId
            );

            renderRanking(getFilteredPlayers());
            setStatus(config.cacheStatusText);
            publishCompetitiveState(
              cleanSeasonId,
              state.players,
              "stale-cache"
            );
            return;
          } catch (cacheError) {
            // Continúa al error final.
          }
        }

        showError(config.apiErrorText);
      }
    }

    function normalizeSeasonsPayload(payload) {
      if (!payload || payload.ok !== true) {
        return [];
      }

      const source = Array.isArray(payload.seasons)
        ? payload.seasons
        : Array.isArray(payload.data)
          ? payload.data
          : [];

      return source
        .map((row, index) => {
          const id = firstNonEmpty(
            row["Temporada ID"],
            row["Season ID"],
            row.seasonId,
            row.id
          );

          if (!id) return null;

          const name = firstNonEmpty(
            row["Temporada"],
            row["Nombre Temporada"],
            row["Nombre"],
            row.name,
            `Temporada ${index + 1}`
          );

          const status = firstNonEmpty(
            row["Estado"],
            row.status,
            ""
          );

          const order = parseNumberSafe(
            firstNonEmpty(
              row["Orden"],
              row.order,
              index + 1
            )
          );

          const tournamentsRegistered = parseNumberSafe(
            firstNonEmpty(
              row["Torneos Registrados"],
              row.tournamentsRegistered,
              0
            )
          );

          const tournamentsExpected = Math.max(
            1,
            parseNumberSafe(
              firstNonEmpty(
                row["Torneos Esperados"],
                row.tournamentsExpected,
                18
              )
            ) || 18
          );

          const activeValue = normalizeSearchText(
            firstNonEmpty(
              row.Activa,
              row.active,
              ""
            )
          );

          return {
            id: String(id).trim(),
            name: String(name).trim(),
            status: String(status).trim(),
            order: Number.isFinite(order)
              ? order
              : index + 1,
            tournamentsRegistered,
            tournamentsExpected,
            active:
              activeValue === "true" ||
              activeValue === "1" ||
              normalizeSearchText(status) === "activa" ||
              normalizeSearchText(status) === "activo",
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a.order !== b.order) {
            return a.order - b.order;
          }

          return a.name.localeCompare(
            b.name,
            "es",
            { sensitivity: "base" }
          );
        });
    }

    function normalizeRankingPayload(payload, expectedSeason) {
      if (!payload || payload.ok !== true) {
        throw new Error(
          payload && payload.error
            ? payload.error
            : "INVALID_API_RESPONSE"
        );
      }

      const responseSeason = String(
        payload.season || expectedSeason || ""
      ).trim();

      if (
        expectedSeason &&
        responseSeason &&
        responseSeason !== expectedSeason
      ) {
        throw new Error("SEASON_MISMATCH");
      }

      const source = Array.isArray(payload.ranking)
        ? payload.ranking
        : [];

      const players = source
        .map((row) => {
          const id = firstNonEmpty(
            row.ID,
            row["Jugador ID"],
            row.id
          );

          const nombre = firstNonEmpty(
            row.Nombre,
            row.Jugador,
            row.nombre
          );

          if (!id || !nombre) {
            return null;
          }

          const puntosNum = parseNumberSafe(
            firstNonEmpty(
              row.Puntos,
              row.puntos,
              0
            )
          );

          const rankingNum = parseNumberSafe(
            firstNonEmpty(
              row.Ranking,
              row.ranking,
              0
            )
          );

          return {
            id: String(id).trim(),
            nombre: String(nombre).trim(),

            puntos: formatPoints(puntosNum),
            puntosNum,

            ranking:
              rankingNum > 0
                ? Math.trunc(rankingNum)
                : Infinity,

            seasonId: firstNonEmpty(
              row["Temporada ID"],
              expectedSeason,
              responseSeason
            ),

            avatar: firstNonEmpty(
              row.Avatar,
              ""
            ),

            winRate: parseNumberSafe(
              firstNonEmpty(
                row["Win Rate"],
                0
              )
            ),

            torneosJugados: parseNumberSafe(
              firstNonEmpty(
                row["Torneos Jugados"],
                0
              )
            ),

            nivelCompetitivo: firstNonEmpty(
              row["Nivel Competitivo"],
              ""
            ),

            estadoVisual: firstNonEmpty(
              row["Estado Visual"],
              ""
            ),
          };
        })
        .filter(Boolean);

      players.sort((a, b) => {
        const rankA = Number.isFinite(a.ranking)
          ? a.ranking
          : 999999;

        const rankB = Number.isFinite(b.ranking)
          ? b.ranking
          : 999999;

        if (rankA !== rankB) {
          return rankA - rankB;
        }

        if (b.puntosNum !== a.puntosNum) {
          return b.puntosNum - a.puntosNum;
        }

        return a.nombre.localeCompare(
          b.nombre,
          "es",
          { sensitivity: "base" }
        );
      });

      return players;
    }

    function renderSeasonButtons() {
      elements.seasonButtonsWrap.innerHTML = "";

      if (!state.seasons.length) {
        return;
      }

      const fragment = document.createDocumentFragment();

      state.seasons.forEach((season) => {
        const button = document.createElement("button");

        button.type = "button";
        button.className = "mf-btn";
        button.dataset.season = season.id;
        button.dataset.estado = season.status || "";

        button.textContent = formatSeasonLabel(season);

        button.setAttribute(
          "aria-label",
          `Ver ranking de ${season.name}`
        );

        button.addEventListener("click", async () => {
          if (season.id === state.activeSeason) {
            return;
          }

          state.expanded = Boolean(state.filterText);

          await loadRanking(season.id);
          scrollToRankingTop();
        });

        fragment.appendChild(button);
      });

      elements.seasonButtonsWrap.appendChild(fragment);
      highlightActiveSeasonButton();
    }

    function formatSeasonLabel(season) {
      const status = normalizeSearchText(
        season.status
      );

      if (
        status === "activa" ||
        status === "activo"
      ) {
        return `🏆 ${season.name}`;
      }

      if (
        status === "cerrada" ||
        status === "cerrado" ||
        status === "historico" ||
        status === "historico"
      ) {
        return `⚔️ ${season.name}`;
      }

      return season.name;
    }

    function highlightActiveSeasonButton() {
      elements.seasonButtonsWrap
        .querySelectorAll("button.mf-btn")
        .forEach((button) => {
          const active =
            button.dataset.season ===
            state.activeSeason;

          button.classList.toggle(
            "is-active",
            active
          );

          button.setAttribute(
            "aria-pressed",
            active ? "true" : "false"
          );
        });
    }

    function chooseDefaultSeason(seasons) {
      if (!Array.isArray(seasons) || !seasons.length) {
        return "";
      }

      const configured = seasons.find(
        (season) =>
          season.id === config.defaultSeason
      );

      if (configured) {
        return configured.id;
      }

      const active = seasons.find((season) => {
        const status = normalizeSearchText(
          season.status
        );

        return (
          status === "activa" ||
          status === "activo"
        );
      });

      if (active) {
        return active.id;
      }

      return seasons[0].id;
    }

    function getFilteredPlayers() {
      if (!state.filterText) {
        return state.players.slice();
      }

      return state.players.filter((player) => {
        return normalizeSearchText(
          player.nombre
        ).includes(state.filterText);
      });
    }

    function renderRanking(players) {
      renderDesktopTable(players);
      renderMobileCards(players);
      updateLoadMore(players);
    }

    function renderDesktopTable(players) {
      elements.tableBody.innerHTML = "";

      if (!players.length) {
        elements.tableBody.innerHTML = `
          <tr>
            <td colspan="4" style="text-align:center;">
              ${escapeHtml(config.emptyText)}
            </td>
          </tr>
        `;

        return;
      }

      const fragment =
        document.createDocumentFragment();

      const visibleLimit = state.expanded
        ? players.length
        : Math.min(
            players.length,
            config.visibleRowsInitial
          );

      players.forEach((player, index) => {
        const visible = index < visibleLimit;

        const profileUrl =
          buildProfileUrl(player);

        const profileLabel =
          `Ver perfil de ${player.nombre}`;

        const row =
          document.createElement("tr");

        row.className = visible
          ? "revelado"
          : "oculto";

        row.dataset.profileUrl = profileUrl;

        if (
          Number.isFinite(player.ranking) &&
          player.ranking <= 3
        ) {
          row.classList.add(
            `ranking-top-${player.ranking}`
          );
        }

        row.addEventListener(
          "click",
          (event) => {
            if (event.target.closest("a")) {
              return;
            }

            window.location.href = profileUrl;
          }
        );

        const rankCell =
          document.createElement("td");

        rankCell.textContent =
          getRankDisplay(player);

        const nameCell =
          document.createElement("td");

        nameCell.textContent =
          player.nombre;

        const pointsCell =
          document.createElement("td");

        pointsCell.textContent =
          player.puntos;

        const profileCell =
          document.createElement("td");

        profileCell.className =
          "profile-cell";

        const profileLink =
          document.createElement("a");

        profileLink.href = profileUrl;
        profileLink.className =
          "profile-link";

        profileLink.setAttribute(
          "aria-label",
          profileLabel
        );

        profileLink.setAttribute(
          "title",
          profileLabel
        );

        profileLink.innerHTML = `
          <i
            class="fas fa-user"
            aria-hidden="true"
          ></i>
          <span class="profile-link-label">
            Perfil
          </span>
        `;

        attachProfilePrefetchIntent(profileLink, player);
        profileCell.appendChild(profileLink);

        row.append(
          rankCell,
          nameCell,
          pointsCell,
          profileCell
        );

        fragment.appendChild(row);
      });

      elements.tableBody.appendChild(fragment);
    }

    function renderMobileCards(players) {
      elements.mobileList.innerHTML = "";

      if (!players.length) {
        return;
      }

      const visibleLimit = state.expanded
        ? players.length
        : Math.min(
            players.length,
            config.visibleRowsInitial
          );

      const fragment =
        document.createDocumentFragment();

      players
        .slice(0, visibleLimit)
        .forEach((player) => {
          const profileUrl =
            buildProfileUrl(player);

          const profileLabel =
            `Ver perfil de ${player.nombre}`;

          const card =
            document.createElement("a");

          card.className = "player-card";
          card.href = profileUrl;

          card.setAttribute(
            "aria-label",
            profileLabel
          );

          card.setAttribute(
            "title",
            profileLabel
          );

          attachProfilePrefetchIntent(card, player);

          if (
            Number.isFinite(player.ranking) &&
            player.ranking <= 3
          ) {
            card.classList.add(
              `player-card-top-${player.ranking}`
            );
          }

          card.innerHTML = `
            <div class="player-card-main">

              <div class="player-rank">
                ${escapeHtml(
                  getRankDisplay(player)
                )}
              </div>

              <div class="player-info">

                <div class="player-name">
                  ${escapeHtml(
                    player.nombre
                  )}
                </div>

                <div class="player-meta-row">

                  <span class="player-points-badge">
                    ${escapeHtml(
                      player.puntos
                    )} pts temporada
                  </span>

                  <span class="player-meta-hint">
                    Ver perfil de competidor
                  </span>

                </div>

              </div>

            </div>

            <div class="player-chevron">
              <i
                class="fa-solid fa-chevron-right"
                aria-hidden="true"
              ></i>
            </div>
          `;

          fragment.appendChild(card);
        });

      elements.mobileList.appendChild(fragment);
    }

    function updateLoadMore(players) {
      const hasMore =
        players.length >
        config.visibleRowsInitial;

      elements.loadMoreWrap.style.display =
        hasMore ? "block" : "none";

      elements.loadMoreBtn.setAttribute(
        "aria-expanded",
        state.expanded ? "true" : "false"
      );

      elements.loadMoreBtn.textContent =
        state.expanded
          ? config.loadLessLabel
          : config.loadMoreLabel;
    }

    function getRankDisplay(player) {
      if (!Number.isFinite(player.ranking)) {
        return "-";
      }

      if (player.ranking === 1) {
        return "🥇 1";
      }

      if (player.ranking === 2) {
        return "🥈 2";
      }

      if (player.ranking === 3) {
        return "🥉 3";
      }

      return String(player.ranking);
    }

    function buildProfileUrl(player) {
      const url = new URL(
        config.profileBaseUrl,
        window.location.origin
      );

      url.searchParams.set(
        config.playerIdQueryParam,
        player.id
      );

      if (state.activeSeason) {
        url.searchParams.set(
          config.seasonQueryParam,
          state.activeSeason
        );
      }

      return `${url.pathname}${url.search}${url.hash}`;
    }

    function getActiveSeasonMeta(seasonId) {
      return (
        state.seasons.find(
          (season) => season.id === seasonId
        ) || {
          id: seasonId,
          name: seasonId || "Temporada",
          status: "",
          order: 0,
          tournamentsRegistered: 0,
          tournamentsExpected: 18,
          active: false,
        }
      );
    }

    function getRankingLeader(players) {
      if (!Array.isArray(players) || !players.length) {
        return {
          id: "",
          name: "Por definir",
          points: 0,
          ranking: 0,
        };
      }

      const leader =
        players.find(
          (player) =>
            Number.isFinite(player.ranking) &&
            player.ranking === 1
        ) ||
        players[0];

      return {
        id: leader?.id || "",
        name: leader?.nombre || "Por definir",
        points: parseNumberSafe(leader?.puntosNum),
        ranking: Number.isFinite(leader?.ranking)
          ? leader.ranking
          : 0,
      };
    }

    function publishCompetitiveState(
      seasonId,
      players,
      source
    ) {
      const season =
        getActiveSeasonMeta(seasonId);

      const current =
        parseNumberSafe(
          season.tournamentsRegistered
        );

      const total =
        Math.max(
          1,
          parseNumberSafe(
            season.tournamentsExpected
          ) || 18
        );

      const payload = {
        version: 2,
        savedAt: Date.now(),
        source: source || "unknown",
        season: {
          id: season.id || seasonId || "",
          name:
            season.name ||
            season.id ||
            seasonId ||
            "Temporada",
          status:
            season.status || "",
          active:
            Boolean(season.active),
          current,
          total,
          progress:
            Math.max(
              0,
              Math.min(
                100,
                Math.round(
                  (current / total) * 100
                )
              )
            ),
        },
        playerCount:
          Array.isArray(players)
            ? players.length
            : 0,
        leader:
          getRankingLeader(players),
      };

      window.__MF_COMPETITIVE_STATE__ =
        payload;

      try {
        window.localStorage.setItem(
          COMPETITIVE_STATE_KEY,
          JSON.stringify(payload)
        );
      } catch (error) {
        // El evento sigue funcionando aunque localStorage esté bloqueado.
      }

      document.dispatchEvent(
        new CustomEvent(
          COMPETITIVE_EVENT,
          {
            detail: payload,
          }
        )
      );
    }

    function getProfileCacheKey(playerId, seasonId) {
      return `${PROFILE_CACHE_PREFIX}${String(playerId || "").trim().toUpperCase()}:${String(seasonId || "GLOBAL").trim() || "GLOBAL"}`;
    }

    function getProfileCacheRecord(playerId, seasonId) {
      try {
        const raw = window.localStorage.getItem(
          getProfileCacheKey(playerId, seasonId)
        );

        if (!raw) return null;

        const record = JSON.parse(raw);

        if (
          !record ||
          record.v !== PROFILE_CACHE_VERSION ||
          !record.savedAt ||
          !record.data
        ) {
          return null;
        }

        const age = Date.now() - Number(record.savedAt);

        if (
          !Number.isFinite(age) ||
          age < 0 ||
          age > PROFILE_CACHE_MAX_AGE_MS
        ) {
          return null;
        }

        return record;
      } catch (error) {
        return null;
      }
    }

    function setProfileCacheRecord(playerId, seasonId, payload) {
      if (!playerId || !payload) return;

      try {
        window.localStorage.setItem(
          getProfileCacheKey(playerId, seasonId),
          JSON.stringify({
            v: PROFILE_CACHE_VERSION,
            savedAt: Date.now(),
            data: payload,
          })
        );
      } catch (error) {
        // El ranking y el perfil siguen funcionando sin localStorage.
      }
    }

    function writeProfileCompatibleSeasonCache(seasonId) {
      const cleanSeason = String(seasonId || "").trim();

      if (!cleanSeason) return;

      try {
        window.localStorage.setItem(
          ACTIVE_SEASON_CACHE_KEY,
          JSON.stringify({
            v: PROFILE_CACHE_VERSION,
            savedAt: Date.now(),
            data: {
              seasonId: cleanSeason,
            },
          })
        );
      } catch (error) {
        // Sin impacto funcional.
      }
    }

    function attachProfilePrefetchIntent(element, player) {
      if (!element || !player?.id) return;

      const queue = () => {
        queueProfilePrefetch(player);
      };

      element.addEventListener("pointerenter", queue, {
        once: true,
        passive: true,
      });

      element.addEventListener("touchstart", queue, {
        once: true,
        passive: true,
      });

      element.addEventListener("focus", queue, {
        once: true,
      });
    }

    function scheduleInitialProfilePrefetch(players) {
      if (!Array.isArray(players) || !players.length) {
        return;
      }

      const candidates = players
        .slice(0, PROFILE_PREFETCH_INITIAL)
        .filter((player) => player?.id);

      const schedule = () => {
        candidates.forEach(queueProfilePrefetch);
      };

      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(schedule, {
          timeout: 1800,
        });
      } else {
        window.setTimeout(schedule, 900);
      }
    }

    function queueProfilePrefetch(player) {
      const playerId = String(player?.id || "").trim();
      const seasonId = String(state.activeSeason || "").trim();

      if (!playerId || !seasonId) return;

      const queueKey =
        `${playerId.toUpperCase()}:${seasonId}`;

      if (
        state.prefetchQueued.has(queueKey) ||
        getProfileCacheRecord(playerId, seasonId)
      ) {
        return;
      }

      state.prefetchQueued.add(queueKey);
      state.prefetchQueue.push({
        playerId,
        seasonId,
        queueKey,
      });

      runProfilePrefetchQueue();
    }

    function runProfilePrefetchQueue() {
      while (
        state.prefetchActive < PROFILE_PREFETCH_CONCURRENCY &&
        state.prefetchQueue.length
      ) {
        const job = state.prefetchQueue.shift();

        state.prefetchActive += 1;

        prefetchProfile(job)
          .catch(() => {
            // Prefetch es oportunista: nunca debe mostrar error al usuario.
          })
          .finally(() => {
            state.prefetchActive -= 1;
            runProfilePrefetchQueue();
          });
      }
    }

    async function prefetchProfile(job) {
      if (!job?.playerId || !job?.seasonId) {
        return;
      }

      if (
        getProfileCacheRecord(
          job.playerId,
          job.seasonId
        )
      ) {
        return;
      }

      const payload = await fetchJson(
        buildApiUrl("profile", {
          id: job.playerId,
          season: job.seasonId,
        })
      );

      setProfileCacheRecord(
        job.playerId,
        job.seasonId,
        payload
      );
    }

    function buildApiUrl(action, params = {}) {
      const url = new URL(config.apiUrl);

      url.searchParams.set(
        "action",
        action
      );

      Object.entries(params).forEach(
        ([key, value]) => {
          const cleanValue =
            String(value ?? "").trim();

          if (cleanValue) {
            url.searchParams.set(
              key,
              cleanValue
            );
          }
        }
      );

      return url.toString();
    }

    async function fetchJson(url) {
      const controller =
        new AbortController();

      const timeout = window.setTimeout(
        () => controller.abort(),
        REQUEST_TIMEOUT_MS
      );

      try {
        const response = await fetch(url, {
          method: "GET",
          signal: controller.signal,
          cache: "no-store",
          credentials: "omit",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `HTTP_${response.status}`
          );
        }

        const text =
          await response.text();

        let payload;

        try {
          payload = JSON.parse(text);
        } catch (error) {
          throw new Error(
            "INVALID_JSON"
          );
        }

        if (!payload || payload.ok !== true) {
          throw new Error(
            payload && payload.error
              ? payload.error
              : "API_ERROR"
          );
        }

        return payload;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    function setLoadingState() {
      elements.tableBody.innerHTML = `
        <tr>
          <td
            colspan="4"
            style="text-align:center;"
          >
            ${escapeHtml(
              config.loadingText
            )}
          </td>
        </tr>
      `;

      elements.mobileList.innerHTML = "";
      elements.loadMoreWrap.style.display =
        "none";

      setStatus(config.loadingText);
    }

    function showError(message) {
      const safeMessage =
        message || config.apiErrorText;

      elements.tableBody.innerHTML = `
        <tr>
          <td
            colspan="4"
            style="
              text-align:center;
              color:#fecaca;
            "
          >
            ${escapeHtml(safeMessage)}
          </td>
        </tr>
      `;

      elements.mobileList.innerHTML = "";
      elements.loadMoreWrap.style.display =
        "none";

      setStatus(safeMessage);
    }

    function setStatus(message) {
      elements.statusNode.textContent =
        message || "";
    }

    function setQueryParam(key, value) {
      if (!key) return;

      const url =
        new URL(window.location.href);

      const cleanValue =
        String(value || "").trim();

      if (cleanValue) {
        url.searchParams.set(
          key,
          cleanValue
        );
      } else {
        url.searchParams.delete(key);
      }

      window.history.replaceState(
        {},
        "",
        `${url.pathname}${url.search}${url.hash}`
      );
    }

    function scrollToRankingTop() {
      root.scrollIntoView({
        behavior: prefersReducedMotion()
          ? "auto"
          : "smooth",
        block: "start",
      });
    }

    function getCacheKey(suffix) {
      return [
        "mf_competitive",
        "ranking",
        "v2",
        suffix,
      ].join(":");
    }

    function getCache(key) {
      try {
        const raw =
          window.localStorage.getItem(key);

        return raw
          ? JSON.parse(raw)
          : null;
      } catch (error) {
        return null;
      }
    }

    function setCache(key, data) {
      try {
        window.localStorage.setItem(
          key,
          JSON.stringify({
            time: Date.now(),
            data,
          })
        );
      } catch (error) {
        // La sección funciona sin localStorage.
      }
    }

    function isCacheFresh(cache) {
      if (!cache || !cache.time) {
        return false;
      }

      return (
        Date.now() - cache.time <
        CACHE_TTL_MS
      );
    }

    function isCacheUsable(cache) {
      if (!cache || !cache.time || !cache.data) {
        return false;
      }

      const age = Date.now() - Number(cache.time);

      return (
        Number.isFinite(age) &&
        age >= 0 &&
        age < CACHE_STALE_MAX_AGE_MS
      );
    }

    function normalizeSearchText(value) {
      return String(value || "")
        .normalize("NFKD")
        .replace(
          /[\u0300-\u036f]/g,
          ""
        )
        .toLowerCase()
        .trim();
    }

    function parseNumberSafe(value) {
      if (
        typeof value === "number" &&
        Number.isFinite(value)
      ) {
        return value;
      }

      let text =
        String(value ?? "").trim();

      if (!text) {
        return 0;
      }

      text = text
        .replace(/\s/g, "")
        .replace(
          /(?<=\d)[.,](?=\d{3}\b)/g,
          ""
        );

      if (
        text.includes(",") &&
        !text.includes(".")
      ) {
        text =
          text.replace(",", ".");
      }

      const number =
        Number.parseFloat(text);

      return Number.isFinite(number)
        ? number
        : 0;
    }

    function formatPoints(value) {
      const number =
        parseNumberSafe(value);

      if (Number.isInteger(number)) {
        return String(number);
      }

      return String(
        Math.round(
          number * 100
        ) / 100
      );
    }

    function firstNonEmpty(...values) {
      for (const value of values) {
        if (
          value !== undefined &&
          value !== null &&
          String(value).trim() !== ""
        ) {
          return value;
        }
      }

      return "";
    }

    function isValidApiUrl(value) {
      try {
        const url = new URL(value);

        return (
          url.protocol === "https:" ||
          url.protocol === "http:"
        );
      } catch (error) {
        return false;
      }
    }

    function prefersReducedMotion() {
      return Boolean(
        window.matchMedia &&
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches
      );
    }

    function escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      { once: true }
    );
  } else {
    boot();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      const section =
        event.target;

      if (!section) return;

      if (
        section.matches &&
        section.matches(ROOT_SELECTOR)
      ) {
        section.dataset.msiRankingInitialized =
          "false";

        boot();
        return;
      }

      const root =
        section.querySelector?.(
          ROOT_SELECTOR
        );

      if (root) {
        root.dataset.msiRankingInitialized =
          "false";

        boot();
      }
    }
  );
})();